import { useMemo, useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import { Box, Snackbar, Alert } from "@mui/material";
import BlockEditorModal from "./BlockEditorModal";
import { updateChunkContent } from "../../api/chunkRoutes";

const A3_WIDTH_PX = 1123;

export default function HtmlViewer({
                                       page,
                                       hoveredChunkId,
                                       setHoveredChunkId,
                                       leftWidth,
                                   }) {
    const containerRef = useRef(null);
    const iframeRef = useRef(null);

    const [scale, setScale] = useState(1);
    const [contentHeight, setContentHeight] = useState(A3_WIDTH_PX * 1.414);

    // --- click-to-edit state ---
    const [selectedBlock, setSelectedBlock] = useState(null); 
    const [anchorRect, setAnchorRect] = useState(null);
    const [viewerBounds, setViewerBounds] = useState(null); 
    const editedChunksRef = useRef({});
    const originalTextRef = useRef({});
    const [saveError, setSaveError] = useState(null);

    /*
    |--------------------------------------------------------------------------
    | Build complete HTML document
    |--------------------------------------------------------------------------
    */

    const safeHtml = useMemo(() => {
        let html = page?.html_content || "";

        if (!html) return "";

        html = DOMPurify.sanitize(html, {
            WHOLE_DOCUMENT: true,

            ADD_TAGS: [
                "html",
                "head",
                "body",
                "style",
                "meta",
                "link",
                "figure",
            ],

            ADD_ATTR: [
                "style",
                "class",
                "id",
                "dir",
                "lang",
                "href",
                "rel",
                "src",
                "alt",
                "loading",
                "data-chunk-id",
                "data-type",
            ],
        });

        return html;
    }, [page?.html_content]);

    /*
    |--------------------------------------------------------------------------
    | Calculate scale
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return;

        const applyScaleFromWidth = (width) => {
            if (!width) return;
            const newScale = Math.min(width / A3_WIDTH_PX, 1);
            setScale(Number(newScale.toFixed(3)));
        };

        applyScaleFromWidth(node.clientWidth);

        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                applyScaleFromWidth(entry.contentRect.width);
            }
        });

        ro.observe(node);

        return () => ro.disconnect();
    }, [leftWidth]);

    /*
    |--------------------------------------------------------------------------
    | Click-to-edit helpers
    |--------------------------------------------------------------------------
    */

    const extractPlainText = (container) => {
        const nodes = Array.from(
            container.querySelectorAll("p, h1, h2, h3, h4, h5, h6")
        );
        if (!nodes.length) return container.innerText.trim();
        return nodes.map((n) => n.innerText.trim()).join("\n\n");
    };

    const applyContentToDom = (chunkId, content) => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;

        const chunkEl = doc.querySelector(
            `[data-chunk-id="${CSS.escape(chunkId)}"]`
        );
        if (!chunkEl) return;

        const nodes = Array.from(
            chunkEl.querySelectorAll("p, h1, h2, h3, h4, h5, h6")
        );
        const paragraphs = content.split(/\n\s*\n/);

        if (nodes.length && nodes.length === paragraphs.length) {
            nodes.forEach((node, i) => {
                node.textContent = paragraphs[i];
            });
        } else if (nodes.length) {
            nodes[0].textContent = content;
            nodes.slice(1).forEach((n) => {
                n.textContent = "";
            });
        }
    };

    const handleLiveChange = (chunkId, content) => {
        applyContentToDom(chunkId, content);
    };

    const handleSaveBlock = (chunkId, content) => {
        const previousContent = editedChunksRef.current[chunkId];
        editedChunksRef.current[chunkId] = content;
        applyContentToDom(chunkId, content);
        setSelectedBlock(null);
        setAnchorRect(null);

        updateChunkContent(chunkId, content).catch((error) => {
            console.error(`Failed to save chunk ${chunkId}`, error);
            if (previousContent !== undefined) {
                editedChunksRef.current[chunkId] = previousContent;
                applyContentToDom(chunkId, previousContent);
            }
            setSaveError(chunkId);
        });
    };

    const handleCloseModal = () => {
        setSelectedBlock(null);
        setAnchorRect(null);
    };

    /*
    |--------------------------------------------------------------------------
    | Iframe load: text fitting + hover + click handlers
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const iframe = iframeRef.current;

        if (!iframe) return;

        const fitTextInBlocks = (doc) => {
    /*
     * --------------------------------------------------------------------------
     * CONFIGURATION
     * --------------------------------------------------------------------------
     */

    const MIN_FONT_SIZE = 6;
    const MAX_FONT_SIZE = 30;
    const DEFAULT_FONT_SIZE = 14;

    /*
     * Small tolerance for browser sub-pixel calculations.
     */
    const TOLERANCE = 1.5;

    /*
     * Title sizes relative to paragraph size.
     */
    const TITLE_SCALE = {
        h1: 1.65,
        h2: 1.35,
        h3: 1.20,
        h4: 1.10,
        h5: 1.00,
        h6: 0.95,
    };

    /*
     * --------------------------------------------------------------------------
     * BASIC HELPERS
     * --------------------------------------------------------------------------
     */

    const getTag = (element) => {
        return element?.tagName?.toLowerCase() || "";
    };

    const isParagraph = (element) => {
        return getTag(element) === "p";
    };

    const isTitle = (element) => {
        const tag = getTag(element);

        return (
            tag === "h1" ||
            tag === "h2" ||
            tag === "h3" ||
            tag === "h4" ||
            tag === "h5" ||
            tag === "h6"
        );
    };

    const getElementDepth = (element) => {
        let depth = 0;
        let current = element;

        while (
            current &&
            current.parentElement
        ) {
            depth++;
            current = current.parentElement;
        }

        return depth;
    };

    const getDeepestTextContainer = (block) => {
        const containers = Array.from(
            block.querySelectorAll(".text-chunk")
        );

        if (!containers.length) {
            return null;
        }

        const validContainers = containers.filter(
            (container) => {
                return container.querySelector(
                    "p, h1, h2, h3, h4, h5, h6"
                );
            }
        );

        if (!validContainers.length) {
            return null;
        }

        return validContainers.reduce(
            (deepest, current) => {
                return getElementDepth(current) >
                    getElementDepth(deepest)
                    ? current
                    : deepest;
            }
        );
    };

    /*
     * --------------------------------------------------------------------------
     * GET DIRECT TEXT ELEMENTS
     * --------------------------------------------------------------------------
     */

    const getTextElements = (container) => {
        if (!container) {
            return [];
        }

        return Array.from(
            container.children
        ).filter((element) => {
            const tag = getTag(element);

            return (
                tag === "p" ||
                tag === "h1" ||
                tag === "h2" ||
                tag === "h3" ||
                tag === "h4" ||
                tag === "h5" ||
                tag === "h6"
            );
        });
    };

    /*
     * --------------------------------------------------------------------------
     * MARGINALIA
     * --------------------------------------------------------------------------
     */

    const isMarginalia = (block) => {
        return (
            block.classList.contains("marginalia") ||
            block.querySelector(".marginalia") !== null
        );
    };

    /*
     * --------------------------------------------------------------------------
     * GET AVAILABLE BLOCK SIZE
     * --------------------------------------------------------------------------
     */

    const getAvailableSize = (block) => {
        const rect =
            block.getBoundingClientRect();

        const style =
            doc.defaultView.getComputedStyle(
                block
            );

        const paddingLeft =
            parseFloat(style.paddingLeft) || 0;

        const paddingRight =
            parseFloat(style.paddingRight) || 0;

        const paddingTop =
            parseFloat(style.paddingTop) || 0;

        const paddingBottom =
            parseFloat(style.paddingBottom) || 0;

        return {
            width: Math.max(
                0,
                rect.width -
                paddingLeft -
                paddingRight
            ),

            height: Math.max(
                0,
                rect.height -
                paddingTop -
                paddingBottom
            ),
        };
    };

    /*
     * --------------------------------------------------------------------------
     * FORCE LAYOUT
     * --------------------------------------------------------------------------
     */

    const forceLayout = () => {
        void doc.body.offsetHeight;
    };

    /*
     * --------------------------------------------------------------------------
     * APPLY BODY FONT
     * --------------------------------------------------------------------------
     */

    const applyBodyFont = (
        elements,
        fontSize
    ) => {
        elements.forEach((element) => {
            if (!isParagraph(element)) {
                return;
            }

            element.style.fontSize =
                `${fontSize}px`;

            element.style.lineHeight =
                "1.15";
        });
    };

    /*
     * --------------------------------------------------------------------------
     * APPLY COMPLETE HIERARCHY
     * --------------------------------------------------------------------------
     */

    const applyCompleteFont = (
        elements,
        baseFontSize
    ) => {
        elements.forEach((element) => {

            const tag = getTag(element);

            if (tag === "p") {

                element.style.fontSize =
                    `${baseFontSize}px`;

                element.style.lineHeight =
                    "1.15";

                return;
            }

            if (TITLE_SCALE[tag]) {

                element.style.fontSize =
                    `${Math.min(
                        baseFontSize * TITLE_SCALE[tag],
                        MAX_FONT_SIZE
                    )}px`;

                element.style.lineHeight =
                    "1.10";
            }
        });
    };

    /*
     * --------------------------------------------------------------------------
     * MEASURE TEXT
     * --------------------------------------------------------------------------
     */

    const getContentSize = (
        container,
        elements
    ) => {
        if (
            !container ||
            !elements.length
        ) {
            return {
                width: 0,
                height: 0,
            };
        }

        forceLayout();

        let maxWidth = 0;

        elements.forEach((element) => {
            const rect =
                element.getBoundingClientRect();

            maxWidth =
                Math.max(
                    maxWidth,
                    rect.width
                );
        });

        const height =
            Math.max(
                container.scrollHeight,
                container.getBoundingClientRect().height
            );

        return {
            width: maxWidth,
            height,
        };
    };

    /*
     * --------------------------------------------------------------------------
     * CHECK FIT
     * --------------------------------------------------------------------------
     */

    const fitsContainer = (
        container,
        elements,
        availableWidth,
        availableHeight
    ) => {
        if (
            !container ||
            !elements.length
        ) {
            return false;
        }

        forceLayout();

        const {
            width,
            height,
        } = getContentSize(
            container,
            elements
        );

        return (
            width <=
            availableWidth +
            TOLERANCE &&

            height <=
            availableHeight +
            TOLERANCE
        );
    };

    /*
     * --------------------------------------------------------------------------
     * FIND MAXIMUM FONT FOR ONE BLOCK
     * --------------------------------------------------------------------------
     */

    const findBestFontForBlock = (
        block,
        container,
        elements,
        options = {}
    ) => {
        const {
            min = MIN_FONT_SIZE,
            max = MAX_FONT_SIZE,
            apply = applyBodyFont,
        } = options;

        const {
            width,
            height,
        } = getAvailableSize(block);

        if (
            width <= 0 ||
            height <= 0
        ) {
            return min;
        }

        /*
         * First check maximum.
         */
        apply(
            elements,
            max
        );

        if (
            fitsContainer(
                container,
                elements,
                width,
                height
            )
        ) {
            return max;
        }

        /*
         * Check minimum.
         */
        apply(
            elements,
            min
        );

        if (
            !fitsContainer(
                container,
                elements,
                width,
                height
            )
        ) {
            return min;
        }

        let low = min;
        let high = max;
        let best = min;

        while (
            high - low > 0.1
        ) {
            const middle =
                (low + high) / 2;

            apply(
                elements,
                middle
            );

            if (
                fitsContainer(
                    container,
                    elements,
                    width,
                    height
                )
            ) {
                best = middle;
                low = middle;
            } else {
                high = middle;
            }
        }

        apply(
            elements,
            best
        );

        return best;
    };

    /*
     * --------------------------------------------------------------------------
     * FIND ALL TEXT BLOCKS
     * --------------------------------------------------------------------------
     */

    const blocks = Array.from(
        doc.querySelectorAll(
            ".block.text"
        )
    );

    const textBlocks = [];

    blocks.forEach((block) => {
        const container =
            getDeepestTextContainer(
                block
            );

        if (!container) {
            return;
        }

        const elements =
            getTextElements(
                container
            );

        if (!elements.length) {
            return;
        }

        elements.forEach((element) => {
            element.style.fontSize = "";
            element.style.lineHeight = "";
        });

        textBlocks.push({
            block,
            container,
            elements,

            hasParagraph:
                elements.some(
                    isParagraph
                ),

            hasTitle:
                elements.some(
                    isTitle
                ),

            special:
                isMarginalia(
                    block
                ),
        });
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 1
     * --------------------------------------------------------------------------
     */

    const normalBlocks =
        textBlocks.filter((item) => {
            return (
                item.hasParagraph &&
                !item.hasTitle &&
                !item.special
            );
        });

    const individualSizes = [];

    normalBlocks.forEach((item) => {
        const best =
            findBestFontForBlock(
                item.block,
                item.container,
                item.elements
            );

        item.bestFontSize = best;

        individualSizes.push(
            best
        );
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 2
     *
     * Calculate a ROBUST common font size.
     *     *
     * Example:
     *
     *  17
     *  16
     *  16
     *  15
     *  15
     *  14
     *   7   <-- tiny block
     *
     * Minimum = 7  WRONG
     *
     * Median = 15 GOOD
     *
     * --------------------------------------------------------------------------
     */

    const getMedian = (values) => {
        if (!values.length) {
            return DEFAULT_FONT_SIZE;
        }

        const sorted =
            [...values].sort(
                (a, b) => a - b
            );

        const middle =
            Math.floor(
                sorted.length / 2
            );

        if (
            sorted.length % 2 === 0
        ) {
            return (
                sorted[middle - 1] +
                sorted[middle]
            ) / 2;
        }

        return sorted[middle];
    };

    let commonBodyFont =
        getMedian(
            individualSizes
        );

    commonBodyFont =
        Math.round(
            commonBodyFont * 2
        ) / 2;

    commonBodyFont =
        Math.max(
            MIN_FONT_SIZE,
            Math.min(
                MAX_FONT_SIZE,
                commonBodyFont
            )
        );

    /*
     * --------------------------------------------------------------------------
     * STEP 3
     *
     * Apply common font to all normal blocks.
     * --------------------------------------------------------------------------
     */

    normalBlocks.forEach((item) => {
        applyBodyFont(
            item.elements,
            commonBodyFont
        );
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 4
     *
     * IMPORTANT:
     *
     * Some blocks cannot fit the common font.
     *
     * Those blocks are individually reduced.
     *
     * This means:
     *
     * normal blocks ≈ same font
     *
     * but very small blocks can shrink.
     * --------------------------------------------------------------------------
     */

    normalBlocks.forEach((item) => {
        const {
            block,
            container,
            elements,
        } = item;

        const {
            width,
            height,
        } = getAvailableSize(
            block
        );

        if (
            width <= 0 ||
            height <= 0
        ) {
            return;
        }

        if (
            fitsContainer(
                container,
                elements,
                width,
                height
            )
        ) {
            return;
        }

        const best =
            findBestFontForBlock(
                block,
                container,
                elements,
                {
                    min: MIN_FONT_SIZE,
                    max: commonBodyFont,
                    apply: applyBodyFont,
                }
            );

        applyBodyFont(
            elements,
            best
        );
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 5
     * --------------------------------------------------------------------------
     */

    const specialBlocks =
        textBlocks.filter(
            (item) =>
                !normalBlocks.includes(
                    item
                )
        );

    specialBlocks.forEach((item) => {
        const {
            block,
            container,
            elements,
            hasTitle,
            special,
        } = item;

        const {
            width,
            height,
        } = getAvailableSize(
            block
        );

        if (
            width <= 0 ||
            height <= 0
        ) {
            return;
        }

        /*
         * Marginalia stays smaller.
         */
        if (special) {
            const maxSize =
                Math.min(
                    commonBodyFont,
                    10
                );

            const best =
                findBestFontForBlock(
                    block,
                    container,
                    elements,
                    {
                        min:
                            MIN_FONT_SIZE,

                        max:
                            maxSize,

                        apply:
                            applyCompleteFont,
                    }
                );

            applyCompleteFont(
                elements,
                best
            );

            return;
        }

        const maxSize =
            hasTitle
                ? commonBodyFont
                : commonBodyFont;

        const best =
            findBestFontForBlock(
                block,
                container,
                elements,
                {
                    min:
                        MIN_FONT_SIZE,

                    max:
                        maxSize,

                    apply:
                        applyCompleteFont,
                }
            );

        applyCompleteFont(
            elements,
            best
        );
    });

    /*
     * --------------------------------------------------------------------------
     * STEP 6
     * --------------------------------------------------------------------------
     */

    textBlocks.forEach((item) => {
        const {
            block,
            container,
            elements,
        } = item;

        const {
            width,
            height,
        } = getAvailableSize(
            block
        );

        if (
            width <= 0 ||
            height <= 0
        ) {
            return;
        }

        /*
         * Check whether block still overflows.
         */
        if (
            fitsContainer(
                container,
                elements,
                width,
                height
            )
        ) {
            return;
        }

        let currentSize =
            Math.max(
                ...elements.map(
                    (element) => {
                        return (
                            parseFloat(
                                doc.defaultView
                                    .getComputedStyle(
                                        element
                                    )
                                    .fontSize
                            ) ||
                            commonBodyFont
                        );
                    }
                )
            );

        while (
            currentSize >
            MIN_FONT_SIZE
        ) {
            currentSize -= 0.25;

            if (item.hasTitle) {

                const titles = elements.filter(isTitle);

                titles.forEach(title => {

                    const current =
                        parseFloat(
                            doc.defaultView
                                .getComputedStyle(title)
                                .fontSize
                        );

                    title.style.fontSize =
                        `${current - 0.25}px`;
                });

            } else {

                applyBodyFont(
                    elements,
                    currentSize
                );
            }

            if (
                fitsContainer(
                    container,
                    elements,
                    width,
                    height
                )
            ) {
                break;
            }
        }
    });
};

        const fitTablesInBlocks = (doc) => {

            const MIN_FONT_SIZE = 6;
            const MAX_FONT_SIZE = 14;
            const TOLERANCE = 1;

            const blocks = doc.querySelectorAll(".block.table");

            blocks.forEach((block) => {
                const table = block.querySelector("table");
                if (!table) return;

                // Make the table content use the full detected block height
                block.style.overflow = "hidden";
                block.style.boxSizing = "border-box";

                const textChunk = block.querySelector(".text-chunk");
                if (textChunk) {
                    textChunk.style.height = "100%";
                    textChunk.style.width = "100%";
                    textChunk.style.boxSizing = "border-box";
                }

                const tableChunk = block.querySelector(".table-chunk");
                if (tableChunk) {
                    tableChunk.style.height = "100%";
                    tableChunk.style.width = "100%";
                    tableChunk.style.margin = "0";
                    tableChunk.style.boxSizing = "border-box";
                }

                table.style.height = "100%";
                table.style.width = "100%";
                table.style.margin = "0";

                const cells = Array.from(table.querySelectorAll("th, td"));

                const blockStyle = doc.defaultView.getComputedStyle(block);

                const availableHeight =
                    block.clientHeight -
                    (parseFloat(blockStyle.paddingTop) || 0) -
                    (parseFloat(blockStyle.paddingBottom) || 0);

                const availableWidth =
                    block.clientWidth -
                    (parseFloat(blockStyle.paddingLeft) || 0) -
                    (parseFloat(blockStyle.paddingRight) || 0);

                if (availableHeight <= 0 || availableWidth <= 0) return;

                const applySize = (fontSize) => {
                    table.style.setProperty("font-size", `${fontSize}px`, "important");
                    table.style.setProperty("margin", "0", "important");
                    table.style.setProperty("width", "100%", "important");

                    cells.forEach((cell) => {
                        cell.style.setProperty("font-size", `${fontSize}px`, "important");
                        cell.style.setProperty("line-height", "1.1", "important");
                        cell.style.setProperty("padding", "2px 4px", "important");
                        cell.style.setProperty("overflow-wrap", "anywhere");
                    });
                };

                const fits = () => {
                    return (
                        table.getBoundingClientRect().height <= availableHeight + TOLERANCE &&
                        table.scrollWidth <= availableWidth + TOLERANCE
                    );
                };

                let low = MIN_FONT_SIZE;
                let high = MAX_FONT_SIZE;
                let best = MIN_FONT_SIZE;

                applySize(MIN_FONT_SIZE);

                if (!fits()) {
                    console.warn("Table cannot fit even at minimum font size", block.id);
                    return;
                }

                while (high - low > 0.1) {
                    const mid = (low + high) / 2;

                    applySize(mid);

                    if (fits()) {
                        best = mid;
                        low = mid;
                    } else {
                        high = mid;
                    }
                }

                applySize(best);
            });
        };

        const handleLoad = () => {
            const doc =
                iframe.contentDocument;

            if (!doc) return;

            const applyLayout = async () => {
                if (doc.fonts?.ready) {
                    await doc.fonts.ready;
                }

                fitTextInBlocks(doc);

                // Fit table content within original block dimensions
                fitTablesInBlocks(doc);

                const pageElement =
                    doc.querySelector(".page");

                if (pageElement) {
                    const rect =
                        pageElement.getBoundingClientRect();

                    setContentHeight(
                        rect.height
                    );
                } else {
                    setContentHeight(
                        Math.max(
                            doc.body.scrollHeight,
                            doc.documentElement
                                .scrollHeight
                        )
                    );
                }
            };

            applyLayout();

            const handleMouseOver = (event) => {
                const chunk =
                    event.target.closest(
                        "[data-chunk-id]"
                    );

                if (!chunk) return;

                const chunkId =
                    chunk.getAttribute(
                        "data-chunk-id"
                    );

                if (chunkId) {
                    setHoveredChunkId(chunkId);
                }
            };

            const handleMouseLeave = () => {
                setHoveredChunkId(null);
            };

            const handleClick = (event) => {
                const chunk =
                    event.target.closest(
                        "[data-chunk-id]"
                    );

                if (!chunk) return;

                const chunkId =
                    chunk.getAttribute(
                        "data-chunk-id"
                    );

                if (!chunkId) return;

                const content =
                    editedChunksRef.current[chunkId] ??
                    extractPlainText(chunk);

                originalTextRef.current[chunkId] = content;

                const chunkRect =
                    chunk.getBoundingClientRect();

                const iframeRect =
                    iframe.getBoundingClientRect();


                setAnchorRect({
                    top: iframeRect.top + chunkRect.top * scale,
                    left: iframeRect.left + chunkRect.left * scale,
                    width: chunkRect.width * scale,
                    height: chunkRect.height * scale,
                });

                if (containerRef.current) {
                    const bounds = containerRef.current.getBoundingClientRect();

                    setViewerBounds({
                        top: bounds.top,
                        left: bounds.left,
                        width: bounds.width,
                        height: bounds.height,
                    });
                }

                const meta = page?.chunks?.find(
                    (c) => c.external_id === chunkId
                );

                setSelectedBlock({
                    id: chunkId,
                    label: meta?.chunk_type || "text",
                    content,
                });
            };

            doc.addEventListener(
                "mouseover",
                handleMouseOver
            );

            doc.addEventListener(
                "mouseleave",
                handleMouseLeave
            );

            doc.addEventListener(
                "click",
                handleClick
            );

            iframe._cleanup = () => {
                doc.removeEventListener(
                    "mouseover",
                    handleMouseOver
                );

                doc.removeEventListener(
                    "mouseleave",
                    handleMouseLeave
                );

                doc.removeEventListener(
                    "click",
                    handleClick
                );
            };
        };

        iframe.addEventListener(
            "load",
            handleLoad
        );

        return () => {
            iframe.removeEventListener(
                "load",
                handleLoad
            );

            if (iframe._cleanup) {
                iframe._cleanup();
            }
        };
    }, [
        safeHtml,
        setHoveredChunkId,
        scale,
        page,
    ]);

    const updateHover = (doc, chunkId) => {

        if (
            !doc ||
            !doc.head ||
            !doc.documentElement
        ) {
            return;
        }

        let style = doc.getElementById(
            "hover-style"
        );

        if (!style) {
            style = doc.createElement("style");
            style.id = "hover-style";

            doc.head.appendChild(style);
        }

        if (!chunkId) {
            style.innerHTML = "";
            return;
        }


        const escaped = CSS.escape(chunkId);

        style.innerHTML = `
        [data-chunk-id="${escaped}"] {
            outline: 3px solid #deb9ef !important;
            background: rgba(219,197,236,0.2) !important;
        }
    `;
    };

    useEffect(() => {

        const iframe = iframeRef.current;

        if (!iframe) return;

        let cancelled = false;

        const update = () => {

            if (cancelled) return;

            const doc =
                iframe.contentDocument;

            if (!doc || !doc.head) {
                return;
            }

            updateHover(
                doc,
                hoveredChunkId
            );
        };


        update();


        return () => {
            cancelled = true;
        };

    }, [hoveredChunkId]);


    useEffect(() => {
        const iframe = iframeRef.current;

        if (!iframe) return;

        const doc = iframe.contentDocument;

        if (!doc || !doc.head) return;

        if (selectedBlock) {
            updateHover(doc, selectedBlock.id);
        }
    }, [selectedBlock]);

    /*
    |--------------------------------------------------------------------------
    | No page
    |--------------------------------------------------------------------------
    */

    if (!page) {
        return (
            <Box
                sx={{
                    p: 2,
                    color: "#94a3b8",
                }}
            >
                No page selected
            </Box>
        );
    }

    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
            }}
        >
            <Box
                ref={containerRef}
                sx={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    background: "#ffffff",
                }}
            >
                <Box
                    sx={{
                        position: "relative",

                        width: A3_WIDTH_PX * scale,

                        height:
                            contentHeight *
                            scale,

                        minHeight:
                            A3_WIDTH_PX *
                            1.414 *
                            scale,
                    }}
                >
                    <iframe
                        ref={iframeRef}
                        title="OCR Document"
                        srcDoc={safeHtml}
                        sandbox="allow-same-origin"
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: `${A3_WIDTH_PX}px`,
                            height: `${contentHeight}px`,
                            border: "none",
                            transformOrigin:
                                "top left",
                            transform:
                                `scale(${scale})`,
                            display: "block",
                            background:
                                "#ffffff",
                        }}
                    />
                </Box>
            </Box>

            <BlockEditorModal
                block={selectedBlock}
                anchorRect={anchorRect}
                boundsRect={viewerBounds}
                onClose={handleCloseModal}
                onSave={handleSaveBlock}
                onLiveChange={handleLiveChange}
            />

            <Snackbar
                open={Boolean(saveError)}
                autoHideDuration={4000}
                onClose={() => setSaveError(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="error" onClose={() => setSaveError(null)}>
                    Couldn't save that edit — reverted to the last saved version.
                </Alert>
            </Snackbar>
        </Box>
    );
}

HtmlViewer.propTypes = {
    page: PropTypes.object,

    hoveredChunkId:
    PropTypes.string,

    leftWidth:
    PropTypes.string,

    setHoveredChunkId:
    PropTypes.func.isRequired,
};